import React, { useState, useEffect } from 'react';
import { Users, Shield, Settings, Plus, Edit3, Save, X } from 'lucide-react';
import api from '../../services/api';

interface Role {
  id: number;
  role_name: string;
  description: string;
  is_active: boolean;
}

interface Page {
  id: number;
  page_name: string;
  page_path: string;
  page_category: string;
  description: string;
  is_active: boolean;
}

interface Permission {
  page_id: number;
  page_name: string;
  page_path: string;
  page_category: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface User {
  id: number;
  user_name: string;
  user_email: string;
  role: string;
  agency_id?: number;
  agency_name?: string;
  itinerary_team?: boolean;
}

interface Agency {
  agency_id: number;
  agency_name: string;
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'roles' | 'users'>('roles');
  const [showAddRole, setShowAddRole] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState({ role_name: '', description: '' });
  const [newUser, setNewUser] = useState({ user_name: '', user_email: '', role: '', agency_id: undefined as number | undefined, itinerary_team: false });

  useEffect(() => {
    loadRoles();
    loadPages();
    loadUsers();
    loadAgencies();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await api.get('/role-management/roles');
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadPages = async () => {
    try {
      const response = await api.get('/role-management/pages');
      if (response.data.success) {
        setPages(response.data.data);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const loadPermissions = async (roleId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/role-management/permissions/${roleId}`);
      if (response.data.success) {
        setPermissions(response.data.data);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (roleId: number) => {
    setSelectedRole(roleId);
    loadPermissions(roleId);
  };

  const handlePermissionChange = (pageId: number, permission: string, value: boolean) => {
    setPermissions(prev => prev.map(p => 
      p.page_id === pageId 
        ? { ...p, [permission]: value }
        : p
    ));
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      const permissionData = permissions.map(p => ({
        role_id: selectedRole,
        page_id: p.page_id,
        can_view: p.can_view,
        can_edit: p.can_edit,
        can_delete: p.can_delete
      }));

      const response = await api.post('/role-management/permissions', permissionData);
      if (response.data.success) {
        alert('Permissions updated successfully');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Error saving permissions');
    }
  };

  const createRole = async () => {
    try {
      const response = await api.post('/role-management/roles', newRole);
      if (response.data.success) {
        setNewRole({ role_name: '', description: '' });
        setShowAddRole(false);
        loadRoles();
      }
    } catch (error) {
      console.error('Error creating role:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/role-management/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAgencies = async () => {
    try {
      const response = await api.get('/role-management/agencies');
      if (response.data.success) {
        setAgencies(response.data.data);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const createUser = async () => {
    try {
      const response = await api.post('/role-management/users', newUser);
      if (response.data.success) {
        setNewUser({ user_name: '', user_email: '', role: '', agency_id: undefined, itinerary_team: false });
        setShowAddUser(false);
        loadUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const updateUser = async (user: User) => {
    try {
      const response = await api.put(`/role-management/users/${user.id}`, {
        user_name: user.user_name,
        user_email: user.user_email,
        role: user.role,
        agency_id: user.agency_id,
        itinerary_team: user.itinerary_team
      });
      if (response.data.success) {
        setEditingUser(null);
        loadUsers();
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const response = await api.delete(`/role-management/users/${userId}`);
      if (response.data.success) {
        loadUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.page_category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {} as { [key: string]: Permission[] });

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">User & Role Management</h1>
          <p className="text-gray-600">Manage users, roles and page access permissions</p>
          
          {/* Tabs */}
          <div className="flex border-b mt-4">
            <button
              className={`px-4 py-2 font-medium text-sm ${activeTab === 'users' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${activeTab === 'roles' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('roles')}
            >
              🛡️ Roles & Permissions
            </button>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Users
                </h2>
                <button
                  onClick={() => setShowAddUser(true)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add User
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Itinerary Team</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <input
                            type="text"
                            value={editingUser.user_name}
                            onChange={(e) => setEditingUser({...editingUser, user_name: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        ) : (
                          <div className="text-sm font-medium text-gray-900">{user.user_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <input
                            type="email"
                            value={editingUser.user_email}
                            onChange={(e) => setEditingUser({...editingUser, user_email: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        ) : (
                          <div className="text-sm text-gray-900">{user.user_email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <select
                            value={editingUser.role}
                            onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {roles.map(role => (
                              <option key={role.id} value={role.role_name}>{role.role_name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <select
                            value={editingUser.agency_id || ''}
                            onChange={(e) => setEditingUser({...editingUser, agency_id: e.target.value ? parseInt(e.target.value) : undefined})}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="">No Agency</option>
                            {agencies.map(agency => (
                              <option key={agency.agency_id} value={agency.agency_id}>{agency.agency_name}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-sm text-gray-900">{user.agency_name || 'No Agency'}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser?.id === user.id ? (
                          <input
                            type="checkbox"
                            checked={!!editingUser.itinerary_team}
                            onChange={(e) => setEditingUser({...editingUser, itinerary_team: e.target.checked})}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                        ) : (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.itinerary_team ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {user.itinerary_team ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {editingUser?.id === user.id ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateUser(editingUser)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setEditingUser(null)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Roles Panel */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Roles
                </h2>
                <button
                  onClick={() => setShowAddRole(true)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Role
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-2">
              {roles.map(role => (
                <div
                  key={role.id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedRole === role.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleRoleSelect(role.id)}
                >
                  <div className="font-medium text-gray-900">{role.role_name}</div>
                  <div className="text-sm text-gray-600">{role.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions Panel */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Permissions
                  {selectedRole && (
                    <span className="text-sm text-gray-500">
                      - {roles.find(r => r.id === selectedRole)?.role_name}
                    </span>
                  )}
                </h2>
                {selectedRole && (
                  <button
                    onClick={savePermissions}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save Changes
                  </button>
                )}
              </div>
            </div>

            <div className="p-4">
              {!selectedRole ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Select a role to manage permissions</p>
                </div>
              ) : loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading permissions...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedPermissions).map(([category, perms]) => (
                    <div key={category}>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">{category}</h3>
                      <div className="space-y-2">
                        {perms.map(perm => (
                          <div key={perm.page_id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-3">
                            <div>
                              <div className="font-medium text-gray-900">{perm.page_name}</div>
                              <div className="text-sm text-gray-600">{perm.page_path}</div>
                            </div>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={perm.can_view}
                                  onChange={(e) => handlePermissionChange(perm.page_id, 'can_view', e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">View</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={perm.can_edit}
                                  onChange={(e) => handlePermissionChange(perm.page_id, 'can_edit', e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">Edit</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={perm.can_delete}
                                  onChange={(e) => handlePermissionChange(perm.page_id, 'can_delete', e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">Delete</span>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Add Role Modal */}
        {showAddRole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Add New Role</h3>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                  <input
                    type="text"
                    value={newRole.role_name}
                    onChange={(e) => setNewRole({...newRole, role_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter role name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newRole.description}
                    onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Enter role description"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddRole(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createRole}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newUser.user_name}
                  onChange={(e) => setNewUser({...newUser, user_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter user name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.user_email}
                  onChange={(e) => setNewUser({...newUser, user_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.role_name}>{role.role_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
                <select
                  value={newUser.agency_id || ''}
                  onChange={(e) => setNewUser({...newUser, agency_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Agency</option>
                  {agencies.map(agency => (
                    <option key={agency.agency_id} value={agency.agency_id}>{agency.agency_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-user-itinerary-team"
                  checked={newUser.itinerary_team}
                  onChange={(e) => setNewUser({...newUser, itinerary_team: e.target.checked})}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="new-user-itinerary-team" className="text-sm font-medium text-gray-700">
                  Itinerary Team (shows in Team Performance dashboard)
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAddUser(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;